// eslint-disable-next-line jira/filename-case
import React, { useCallback, forwardRef, useMemo, type Ref } from 'react';
import { GrowthRecommendationsEntrypoint } from '@jira/navigation-apps__sidebar-nav4-growth-recommendations/src/GrowthRecommendationsEntrypoint.entrypoint.tsx';
import type { CustomTriggerComponentType } from '@jira/navigation-apps__project-actions-action-menu/src/types.tsx';
import { MenuContainer } from '@jira/navigation-apps__project-actions-action-menu/src/ui/menu-container/index.tsx';
import { MenuContent } from '@jira/navigation-apps__project-actions-action-menu/src/ui/menu-container/MenuContent.tsx';
import { ThemedButtonOld as ThemedButton } from '@jira/platform__project-theme-components/src/ui/themed-button-old/ThemedButtonOld.tsx';
import ShowMoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import ButtonItem from '@atlaskit/menu/button-item';
import type { TriggerProps, ContentProps } from '@atlaskit/popup/types';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useIntl, defineMessage } from '@atlassian/jira-intl';

import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';

const whyAmISeeingThis = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-core.common.recommendations.common.recommendation-action-menu.why-am-i-seeing-this',

	defaultMessage: 'Why am I seeing this?',
	description: 'The label for the link that navigates to the [why am i seeing this] modal',
});

const moreActions = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-core.common.recommendations.common.recommendation-action-menu.more-actions',

	defaultMessage: 'More actions',
	description:
		'Used as the alt text for an icon button that when used shows a popup menu of more options.',
});

const dismissButtonText = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-core.common.recommendations.common.recommendation-action-menu.dismiss-button-text',

	defaultMessage: 'Not interested',
	description:
		'Text for the "Not interested" button in the "setup required" section of the projects dropdown',
});

type Props = {
	onClick?: () => void;
	onLearnMoreClicked: () => void;
	onDismissClicked: () => void;
	body: React.ReactNode | string;
	primaryCta: string;
	title: string;
};

const MENU_TEST_ID = 'recommendations-common.ui.recommendation-popup-menu.menu-group';
// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
const CustomTrigger: CustomTriggerComponentType = forwardRef<HTMLButtonElement, TriggerProps>(
	({ ...props }, ref) => {
		const { formatMessage } = useIntl();

		return (
			<Tooltip hideTooltipOnClick content={formatMessage(moreActions)} position="bottom">
				<ThemedButton
					{...props}
					ref={ref}
					appearance="subtle"
					iconBefore={
						<ShowMoreIcon
							label={formatMessage(moreActions)}
							color="currentColor"
							spacing="spacious"
						/>
					}
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					testId="navigation-apps-sidebar-nav4-sidebars-content-growth-recommendations.common.ui.recommendation-action-menu.themed-button"
				/>
			</Tooltip>
		);
	},
);

type LearnMoreTriggerProps = {
	onLearnMoreClicked: () => void;
};

const LearnMoreTrigger = forwardRef(
	({ onLearnMoreClicked }: LearnMoreTriggerProps, ref: Ref<HTMLButtonElement> | undefined) => {
		const { formatMessage } = useIntl();

		return (
			<ButtonItem ref={ref} onClick={onLearnMoreClicked}>
				{formatMessage(whyAmISeeingThis)}
			</ButtonItem>
		);
	},
);

export const RecommendationActionMenu = ({
	onClick,
	onLearnMoreClicked,
	onDismissClicked,
	body,
	primaryCta,
	title,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const entryPointParams = useMemo(
		() => ({
			body,
			primaryCta,
			title,
		}),
		[body, primaryCta, title],
	);
	const modalProps = useMemo(
		() => ({
			width: 400,
			height: 440,
		}),
		[],
	);

	const MenuItems = useCallback(() => {
		return (
			<MenuGroup testId={MENU_TEST_ID}>
				<Section isList>
					<ButtonItem onClick={() => onDismissClicked()}>
						{formatMessage(dismissButtonText)}
					</ButtonItem>
					<ModalEntryPointPressableTrigger
						entryPoint={GrowthRecommendationsEntrypoint}
						entryPointProps={entryPointParams}
						modalProps={modalProps}
						useInternalModal={false}
					>
						{({ ref }) => (
							<LearnMoreTrigger
								// oxlint-disable-next-line typescript/consistent-type-assertions
								ref={ref as Ref<HTMLButtonElement>}
								onLearnMoreClicked={onLearnMoreClicked}
							/>
						)}
					</ModalEntryPointPressableTrigger>
				</Section>
			</MenuGroup>
		);
	}, [entryPointParams, formatMessage, modalProps, onDismissClicked, onLearnMoreClicked]);

	const content = (contentProps: ContentProps) => (
		<MenuContent {...contentProps} MenuItems={MenuItems} />
	);

	return <MenuContainer content={content} onClick={onClick} CustomTrigger={CustomTrigger} />;
};
